import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import { 
  Upload, FileSpreadsheet, Box, Eye, Layout, 
  CheckCircle, RefreshCw, Database, ChevronRight, List, Building2
} from 'lucide-react';

// --- BIM Viewer Component ---
// 🎯 status جاي من نفس polling بتاع /bim/status/{urn} في المكوّن الأب -
// لو الترجمة لسه شغالة (pending/inprogress) منحاولش نعمل Document.load
// خالص، لأن الموديل مش موجود فعليًا لـ Forge لسه فيرجع مستند فاضي أو
// يفشل بصمت (ده اللي كان بيسبب اللوجات الفاضية في الكونسول) - نعرض
// سبنر بدل كده، والـ useEffect تحت بينفّذ Document.load تلقائي أول ما
// الحالة تتحول لـ 'success' من غير ما اليوزر يعمل أي حاجة
const BIMViewer = ({ urn, status, onRefresh, onMappingReady }) => {
  const { t } = useTranslation();
  const viewerContainer = useRef(null);
  const viewer = useRef(null);
  const isReady = status === 'success';

  useEffect(() => {
    if (!urn || !isReady || !window.Autodesk) return;

    const options = {
      env: 'AutodeskProduction2',
      api: 'streamingV2',
      getAccessToken: async (callback) => {
        try {
          const res = await axios.get('/api/bim/token');
          callback(res.data.access_token, res.data.expires_in);
        } catch (error) {
          console.error("Failed to fetch APS Token", error);
        }
      }
    };

    window.Autodesk.Viewing.Initializer(options, () => {
      viewer.current = new window.Autodesk.Viewing.GuiViewer3D(viewerContainer.current);
      viewer.current.start();
      const documentId = 'urn:' + urn;
      window.Autodesk.Viewing.Document.load(
        documentId,
        (doc) => {
          const viewables = doc.getRoot().getDefaultGeometry();
          viewer.current.loadDocumentNode(doc, viewables);
          // 🎯 دلوقتي جزء حقيقي من منطق الربط: أول ما الموديل يخلص تحميل،
          // بنجيب مجموعة كل GUIDs بتاعته فورًا (model.getExternalIdMapping -
          // جرّبناها فعليًا: 110 ألف GUID في 157ms على موديل حقيقي، بديل
          // أسرع وأوثق من استدعاء Forge REST من السيرفر اللي كان بيضرب 413
          // على موديلات كبيرة) ونبعتها فوق للأب عشان تتبعت مع الكوبي وقت
          // الـ Commit. لو المتصفح ماقدرش (خطأ، موديل قديم مش بيدعمها)،
          // مبنعملش حاجة - السيرفر عنده fallback لـ REST لوحده لأي موديل
          // معندهوش set جاهزة
          if (onMappingReady && viewer.current.model) {
            viewer.current.model.getExternalIdMapping(
              (mapping) => onMappingReady(urn, Object.keys(mapping || {})),
              (error) => console.error(`getExternalIdMapping failed for ${urn}:`, error)
            );
          }
        },
        (errorCode, errorMsg) => {
          console.error(`Forge Document.load failed for ${documentId}: ${errorCode} - ${errorMsg}`);
        }
      );
    });

    return () => {
      if (viewer.current) {
        viewer.current.finish();
        viewer.current = null;
      }
    };
  }, [urn, isReady, onMappingReady]);

  if (!urn) return (
    <div className="flex flex-col items-center justify-center h-full text-slate-500 bg-slate-100/50 rounded-xl border-2 border-dashed border-slate-200">
      <Box size={48} className="mb-4 opacity-20 animate-pulse" />
      <p className="font-bold uppercase tracking-widest text-[10px]">{t('bim.waiting_translation')}</p>
    </div>
  );

  if (!isReady) return (
    <div className="flex flex-col items-center justify-center h-full text-slate-500 bg-slate-100/50 rounded-xl border-2 border-dashed border-slate-200 gap-3">
      <RefreshCw size={48} className={`opacity-40 text-purple-500 ${status === 'failed' ? '' : 'animate-spin'}`} />
      <p className="font-bold uppercase tracking-widest text-[10px]">
        {status === 'failed' ? t('bim.translation_failed', 'Model translation failed') : t('bim.waiting_translation', 'Waiting for Model Translation...')}
      </p>
      {/* 🎯 لو الـ polling الآلي بأي سبب (تبويب اتقفل وفتح تاني، توكن
      انتهى مؤقتًا..) ماوصلش لآخر تحديث، الزرار ده بيعمل نفس فحص
      /bim/status فورًا من غير ما يستنى الـ interval الجاي */}
      {onRefresh && (
        <button onClick={onRefresh} className="flex items-center gap-2 px-4 py-1.5 border border-purple-400 text-purple-600 rounded-lg text-[10px] font-black uppercase hover:bg-purple-50 transition-all">
          <RefreshCw size={12} /> {t('bim.controls.refresh', 'Refresh')}
        </button>
      )}
    </div>
  );

  return <div ref={viewerContainer} className="w-full h-full rounded-xl overflow-hidden shadow-inner bg-black" />;
};

const COBieManager = ({ onImportSuccess }) => {
    const { t, i18n } = useTranslation();
    const [uploading, setUploading] = useState(false);
    const [orgs, setOrgs] = useState([]);
    const [sites, setSites] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [selectedSite, setSelectedSite] = useState('');
    
    const [cobieFile, setCobieFile] = useState(null);
    const [revitFiles, setRevitFiles] = useState([]);
    const [previewData, setPreviewData] = useState([]);
    const [modelUrns, setModelUrns] = useState([]);
    // 🎯 {urn: [externalId, ...]} - بتتجمّع تلقائي أول ما اليوزر يعاين
    // أي موديل جاهز (شوف onMappingReady تحت). بتتبعت مع الكوبي وقت الـ
    // Commit عشان الباك إند يستخدمها بدل ما يستدعي Forge REST بنفسه
    const [modelGuidSets, setModelGuidSets] = useState({});
    // 🎯 useCallback ثابت المرجع - لو بعتنا arrow function جديدة كل render
    // مباشرة كـ prop لـ BIMViewer، الـ useEffect جوّاه (اللي معتمد على
    // onMappingReady في الـ dependency array) هيعتبرها تغيير ويعيد تحميل
    // الفيوور من جديد كل مرة الأب يعمل render - حلقة إعادة تحميل مالها نهاية
    const handleMappingReady = useCallback((urn, guids) => {
        setModelGuidSets(prev => ({ ...prev, [urn]: guids }));
    }, []);
    const [currentPreviewUrn, setCurrentPreviewUrn] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [activeTab, setActiveTab] = useState('3D');
    // 🎯 موديلات ريفيت اتربطت قبل كده بنفس المؤسسة/الموقع - بتسمح بإعادة
    // استخدامها في استيراد كوبي جديد من غير ما اليوزر يرفع نفس ملفات الـ
    // .rvt تاني (الحالة العكسية: أكتر من ملف كوبي لنفس الموديلات)
    const [availableModels, setAvailableModels] = useState([]);
    const [loadingModels, setLoadingModels] = useState(false);
    // 🎯 مؤقّتات الـ polling الشغالة دلوقتي، مفتاحها urn - عشان منبدأش
    // أكتر من polling على نفس الموديل، ونقدر نقفلهم كلهم وقت unmount
    const pollTimersRef = useRef({});

    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    useEffect(() => {
        return () => {
            Object.values(pollTimersRef.current).forEach(clearInterval);
        };
    }, []);

    // 🎯 كل موديل مرفوع لازم نستنى Forge يترجمه فعليًا (status: 'success')
    // قبل ما نحاول نعرضه في الفيوور أو نسمح بالـ Commit - غير كده الفيوور
    // كان بيحاول يفتح موديل لسه مش موجود لـ Forge ويطلع فاضي وبيرمي لوجات
    // خطأ في الكونسول من غير أي إشارة واضحة للمستخدم إن الترجمة لسه شغالة.
    // checkStatusOnce مستخدمة هنا كمان جوه زرار "Refresh" اليدوي في
    // BIMViewer (لو الـ polling الآلي لأي سبب فشل ماحصلش)، فبترجع الحالة
    // نفسها عشان اللي نده عليها يقدر يوري toast مناسب
    const checkStatusOnce = async (urn) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/bim/status/${urn}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const status = res.data.status;
            if (status === 'success' || status === 'failed' || status === 'timeout') {
                if (pollTimersRef.current[urn]) {
                    clearInterval(pollTimersRef.current[urn]);
                    delete pollTimersRef.current[urn];
                }
                setModelUrns(prev => prev.map(m => m.urn === urn ? { ...m, status: status === 'success' ? 'success' : 'failed' } : m));
            } else {
                setModelUrns(prev => prev.map(m => m.urn === urn ? { ...m, status: 'inprogress', progress: res.data.progress } : m));
            }
            return status;
        } catch (err) {
            // 🎯 خطأ عابر (توكن/شبكة) - منوقفش الـ polling عشانه، هيعيد
            // المحاولة تاني بعد الـ interval الجاي من غير ما يفشل الفلو كله
            return null;
        }
    };

    const pollTranslationStatus = (urn) => {
        if (pollTimersRef.current[urn]) return;
        checkStatusOnce(urn);
        pollTimersRef.current[urn] = setInterval(() => checkStatusOnce(urn), 5000);
    };

    const handleManualRefresh = async (urn) => {
        if (!urn) return;
        const status = await checkStatusOnce(urn);
        if (status === 'success') {
            toast.success(t('bim.toasts.model_ready_now', 'Model is ready!'));
        } else if (status === 'failed' || status === 'timeout') {
            toast.error(t('bim.translation_failed', 'Model translation failed'));
        } else if (status) {
            toast(t('bim.still_translating', 'Still translating, please wait...'));
        } else {
            toast.error(t('bim.toasts.status_check_fail', 'Could not check translation status'));
        }
    };

    useEffect(() => {
        axios.get('/api/organizations')
            .then(res => setOrgs(res.data))
            .catch(() => toast.error(t('bim.toasts.load_org_fail')));
    }, [t]);

    const handleOrgChange = (orgId) => {
        setSelectedOrg(orgId);
        const org = orgs.find(o => o.org_id === orgId);
        setSites(org ? org.sites : []);
        setSelectedSite('');
        setAvailableModels([]);
    };

    // 🎯 لما يختار موقع، نجيب موديلات الريفيت اللي اتربطت قبل كده بنفس
    // المؤسسة/الموقع عشان يقدر يعيد استخدامها بدل ما يرفع نفس ملفات .rvt تاني
    useEffect(() => {
        if (!selectedOrg || !selectedSite) { setAvailableModels([]); return; }
        setLoadingModels(true);
        const token = localStorage.getItem('token');
        axios.get('/api/bim/models', {
            params: { org_id: selectedOrg, site_id: selectedSite },
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => setAvailableModels(res.data || []))
            .catch(() => setAvailableModels([]))
            .finally(() => setLoadingModels(false));
    }, [selectedOrg, selectedSite]);

    const toggleExistingModel = (model) => {
        setModelUrns(prev => {
            const exists = prev.some(m => m.urn === model.urn);
            if (exists) return prev.filter(m => m.urn !== model.urn);
            // 🎯 موديل قديم اتربط قبل كده بأصول حقيقية - يبقى أصلاً اترجم
            // بنجاح، مفيش داعي polling ليه تاني
            return [...prev, { name: model.label, urn: model.urn, existing: true, status: 'success' }];
        });
    };

    const handleCobieChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setCobieFile(file);
        
        const formData = new FormData();
        formData.append('file', file);
        
        const loadId = toast.loading(t('bim.toasts.analyzing_excel'));
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/bim/preview-cobie', formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPreviewData(res.data.preview);
            setActiveTab('DATA');
            toast.success(t('bim.toasts.excel_ready'), { id: loadId });
        } catch (err) {
            toast.error(t('bim.toasts.preview_fail'), { id: loadId });
        }
    };

    const handleRevitUpload = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      setIsProcessing(true);
      const lid = toast.loading(`${t('bim.toasts.uploading')} ${files.length} ${t('bim.toasts.models')}...`);
  
      try {
        const urnResults = [];
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('asset_num', 'BIM_IMPORT');

          const token = localStorage.getItem('token');
          const res = await axios.post('/api/bim/upload', formData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          urnResults.push({ name: file.name, urn: res.data.urn, status: 'pending' });
        }

        setModelUrns(prevUrns => [...prevUrns, ...urnResults]);
        urnResults.forEach(r => pollTranslationStatus(r.urn));

        if (urnResults.length > 0) {
          setCurrentPreviewUrn(urnResults[urnResults.length - 1].urn);
        }

        toast.success(t('bim.toasts.models_ready'), { id: lid });
      } catch (err) {
        toast.error(t('bim.toasts.upload_fail'), { id: lid });
      } finally {
        setIsProcessing(false);
      }
    };

    // 🎯 الاستيراد بقى Job في الخلفية (Celery worker) مش request واحد
    // طويل - ملف كبير (35 ألف صف) كان بياخد دقايق ويقفل الـ backend كله
    // عن أي طلب تاني في نفس الوقت. الفرونت إند دلوقتي بيبعت الملف، يستلم
    // job_id فورًا، وبعدين يعمل poll على حالته لحد ما يخلص
    const pollImportJob = (jobId, loadId) => {
        const interval = setInterval(async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`/api/bim/import-cobie/status/${jobId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const { status, error_message, stats } = res.data;
                if (status === 'DONE') {
                    clearInterval(interval);
                    setUploading(false);
                    toast.success(t('bim.toasts.integration_success'), { id: loadId });
                    console.log('COBie import stats:', stats);
                    if (onImportSuccess) onImportSuccess();
                } else if (status === 'FAILED') {
                    clearInterval(interval);
                    setUploading(false);
                    toast.error(`${t('bim.toasts.import_fail')}: ${error_message || ''}`, { id: loadId });
                }
                // PENDING/RUNNING - نسيبها تفضل شغالة، الـ toast الأصلي فاضل زي ما هو
            } catch (err) {
                // خطأ عابر (توكن/شبكة) - مش بنوقف الـ polling عشانه
            }
        }, 3000);
    };

    const executeImport = async () => {
        if (!cobieFile || !selectedSite) return toast.error(t('bim.toasts.select_required'));

        setUploading(true);
        const loadId = toast.loading(t('bim.toasts.finalizing_sync'));

        const formData = new FormData();
        formData.append('file', cobieFile);
        formData.append('org_id', selectedOrg);
        formData.append('site_id', selectedSite);
        formData.append('model_urns', JSON.stringify(modelUrns.map(m => m.urn)));
        // 🎯 GUID sets اللي اتجمّعت من getExternalIdMapping وقت المعاينة -
        // موديل مالوش set هنا (اليوزر معاينهوش) هيرجع الباك إند لـ Forge
        // REST بنفسه كـ fallback، مفيش أي فقدان للربط، بس أبطأ لموديل ده بس
        formData.append('model_guid_sets', JSON.stringify(modelGuidSets));

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/bim/import-cobie', formData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            pollImportJob(res.data.job_id, loadId);
        } catch (err) {
            toast.error(t('bim.toasts.import_fail'), { id: loadId });
            setUploading(false);
        }
    };

    return (
        <div className={`flex flex-col h-full bg-[#f8fafc] border shadow-2xl rounded-2xl overflow-hidden`}>
            {/* Header */}
            <div className={`bg-[#1a3353] p-4 text-white flex justify-between items-center border-b border-blue-400/20`}>
                <div className={`flex items-center gap-4 `}>
                    <div className="p-2 bg-blue-500 rounded-lg shadow-lg"><Layout size={20}/></div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                        <h2 className="text-lg font-black italic tracking-tighter uppercase">{t('bim.title')}</h2>
                        <p className="text-[9px] text-blue-200 font-bold uppercase tracking-widest">{t('bim.subtitle')}</p>
                    </div>
                </div>
                <div className={`flex gap-2 `}>
                   <button onClick={() => setActiveTab('3D')} className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase transition-all ${activeTab === '3D' ? 'bg-blue-600 text-white shadow-md' : 'bg-blue-900/40 text-blue-200'}`}>{t('bim.tabs.3d')}</button>
                   <button onClick={() => setActiveTab('DATA')} className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase transition-all ${activeTab === 'DATA' ? 'bg-green-600 text-white shadow-md' : 'bg-green-900/40 text-green-200'}`}>{t('bim.tabs.data')}</button>
                </div>
            </div>

            <div className={`flex flex-1 overflow-hidden `}>
                {/* Left Control Panel */}
                <div className={`w-[350px] bg-white border-r p-6 flex flex-col gap-6 overflow-y-auto custom-scrollbar ${isRTL ? 'border-l border-r-0' : 'border-r'}`}>
                    <div className="space-y-4">
                        <label className={`text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 `}>
                           <Building2 size={14}/> {t('bim.controls.destination')}
                        </label>
                        <select className={`w-full border-2 p-2.5 rounded-lg text-xs font-bold outline-none focus:border-blue-500 bg-gray-50 ${isRTL ? 'text-right' : 'text-left'}`} value={selectedOrg} onChange={(e) => handleOrgChange(e.target.value)}>
                            <option value="">{t('org_detail.lookups.company_set')}</option>
                            {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.description}</option>)}
                        </select>
                        <select className={`w-full border-2 p-2.5 rounded-lg text-xs font-bold outline-none focus:border-blue-500 bg-gray-50 disabled:opacity-50 ${isRTL ? 'text-right' : 'text-left'}`} value={selectedSite} onChange={(e) => setSelectedSite(e.target.value)} disabled={!selectedOrg}>
                            <option value="">{t('bim.controls.select_site')}</option>
                            {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.description}</option>)}
                        </select>
                    </div>

                    <div className="space-y-4">
                        <label className={`text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 `}>
                           <Upload size={14}/> {t('bim.controls.uploads')}
                        </label>
                        
                        {/* COBie Input */}
                        <div className="relative group">
                            <input type="file" className="absolute inset-0 opacity-0 cursor-pointer z-10" accept=".xlsx" onChange={handleCobieChange} />
                            <div className={`border-2 border-dashed p-4 rounded-xl flex items-center gap-3 transition-all ${cobieFile ? 'border-green-500 bg-green-50' : 'border-gray-200 group-hover:border-blue-400'} ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <FileSpreadsheet className={cobieFile ? 'text-green-600' : 'text-gray-400'} />
                                <span className={`text-[10px] font-black uppercase text-gray-600 truncate w-full ${isRTL ? 'text-right' : 'text-left'}`}>{cobieFile ? cobieFile.name : t('bim.controls.select_cobie')}</span>
                            </div>
                        </div>

                        {/* Revit Input */}
                        <div className="relative group">
                            <input type="file" className="absolute inset-0 opacity-0 cursor-pointer z-10" multiple accept=".rvt" onChange={handleRevitUpload} disabled={isProcessing} />
                            <div className={`border-2 border-dashed p-4 rounded-xl flex items-center gap-3 transition-all ${modelUrns.length > 0 ? 'border-purple-500 bg-purple-50' : 'border-gray-200 group-hover:border-purple-400'} ${isRTL ? 'flex-row-reverse' : ''}`}>
                                {isProcessing ? <RefreshCw className="animate-spin text-purple-600" /> : <Box className={modelUrns.length > 0 ? 'text-purple-600' : 'text-gray-400'} />}
                                <span className={`text-[10px] font-black uppercase text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>{modelUrns.length > 0 ? `${modelUrns.length} ${t('bim.controls.models_ready')}` : t('bim.controls.upload_revit')}</span>
                            </div>
                        </div>
                    </div>

                    {/* 🎯 موديلات اترفعت واترربطت قبل كده لنفس المؤسسة/الموقع - تقدر
                    تختارها بدل ما ترفع نفس ملفات الـ .rvt تاني لو عندك أكتر من ملف
                    كوبي بيغطي نفس الموديلات */}
                    {selectedSite && (
                        <div className="space-y-2">
                            <label className={`text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 `}>
                                <Database size={14}/> {t('bim.controls.existing_models', 'Previously Linked Models')}
                            </label>
                            {loadingModels ? (
                                <p className="text-[9px] text-gray-400 font-bold uppercase">{t('common.loading')}</p>
                            ) : availableModels.length === 0 ? (
                                <p className="text-[9px] text-gray-400 font-bold italic">{t('bim.controls.no_existing_models', 'No models linked yet for this site')}</p>
                            ) : (
                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                                    {availableModels.map((m) => {
                                        const checked = modelUrns.some(u => u.urn === m.urn);
                                        return (
                                            <label key={m.urn} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-white transition-all ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                                                <input type="checkbox" checked={checked} onChange={() => toggleExistingModel(m)} className="accent-purple-600" />
                                                <span className="text-[10px] font-bold text-gray-600 truncate">{m.label}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Preview Models List */}
                    {modelUrns.length > 0 && (
                        <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 space-y-2">
                            <p className={`text-[9px] font-black text-slate-400 uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`}>{t('bim.controls.preview_list')}:</p>
                            {modelUrns.map((m, i) => (
                                <div key={i} onClick={() => {setCurrentPreviewUrn(m.urn); setActiveTab('3D');}} className={`p-2 border rounded-md text-[9px] font-bold cursor-pointer transition-all flex items-center justify-between gap-2 ${currentPreviewUrn === m.urn ? 'bg-white border-blue-500 text-blue-700 shadow-sm' : 'bg-transparent text-gray-500 hover:bg-white'} ${isRTL ? 'text-right' : 'text-left'}`}>
                                    <span className="truncate">{m.name}</span>
                                    {m.status === 'success' && <CheckCircle size={12} className="text-green-500 shrink-0" />}
                                    {(m.status === 'pending' || m.status === 'inprogress') && <RefreshCw size={12} className="text-purple-500 animate-spin shrink-0" />}
                                    {m.status === 'failed' && <span className="text-red-500 text-[8px] font-black shrink-0">{t('bim.translation_failed', 'FAILED')}</span>}
                                </div>
                            ))}
                        </div>
                    )}

                    <button onClick={executeImport} disabled={uploading || !cobieFile || modelUrns.some(m => m.status === 'pending' || m.status === 'inprogress')} className={`w-full bg-[#1a3353] text-white py-4 rounded-xl font-black uppercase tracking-widest text-[11px] hover:bg-blue-900 transition-all shadow-xl disabled:opacity-30 mt-auto flex items-center justify-center gap-2`}>
                        {uploading ? <RefreshCw className="animate-spin" /> : <CheckCircle size={18}/>}
                        {uploading ? t('bim.controls.finalizing') : t('bim.controls.commit')}
                    </button>
                </div>

                {/* Right Viewer Panel */}
                <div className="flex-1 bg-white relative p-4 flex flex-col">
                    {activeTab === '3D' ? (
                        <BIMViewer
                          urn={currentPreviewUrn}
                          status={modelUrns.find(m => m.urn === currentPreviewUrn)?.status}
                          onRefresh={() => handleManualRefresh(currentPreviewUrn)}
                          onMappingReady={handleMappingReady}
                        />
                    ) : (
                        <div className={`flex-1 overflow-auto bg-gray-50 rounded-xl border border-gray-200 p-6 ${isRTL ? 'text-right' : 'text-left'}`}>
                            <div className={`flex items-center gap-4 mb-6 border-b pb-4 `}>
                                <Database className="text-green-600" />
                                <h3 className="text-lg font-black text-gray-800 uppercase italic">{t('bim.preview_table_title')}</h3>
                            </div>
                            <table className="w-full text-[10px]">
                                <thead className={`bg-white border-b uppercase font-black text-gray-400 sticky top-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                                    <tr className={isRTL ? 'flex-row-reverse' : ''}>
                                      <th className="p-3">{t('bim.table.asset_name')}</th>
                                      <th className="p-3">{t('bim.table.location')}</th>
                                      <th className="p-3">{t('organizations.description')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.map((row, i) => (
                                        <tr key={i} className={`border-b hover:bg-blue-50 transition-colors ${isRTL ? ' text-right' : 'text-left'}`}>
                                            <td className="p-3 font-bold text-blue-900">{row.Name}</td>
                                            <td className="p-3 italic text-gray-500">{row.Space}</td>
                                            <td className="p-3 text-gray-400">{row.Description || '---'}</td>
                                        </tr>
                                    ))}
                                    {previewData.length === 0 && <tr><td colSpan="3" className="p-20 text-center italic text-gray-300">{t('bim.toasts.upload_first')}</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default COBieManager;